<template>
  <div class="glass-textarea" :class="{ 'has-error': hasError, 'is-disabled': disabled, 'is-focused': isFocused }">
    <label v-if="label" :for="inputId" class="textarea-label">
      {{ label }}
      <span v-if="required" class="required">*</span>
    </label>
    
    <div class="textarea-wrapper">
      <textarea
        :id="inputId"
        ref="textarea"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :rows="rows"
        :maxlength="maxlength"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        class="textarea-field"
      ></textarea>
      
      <span v-if="maxlength" class="char-count" :class="{ 'is-limit': isAtLimit }">
        {{ charCount }}/{{ maxlength }}
      </span>
    </div>
    
    <span v-if="hasError && errorMessage" class="error-message">
      {{ errorMessage }}
    </span>
    <span v-else-if="description" class="description">
      {{ description }}
    </span>
  </div>
</template>

<script>
let textareaCounter = 0;

export default {
  name: 'GlassTextarea',
  emits: ['update:modelValue', 'focus', 'blur'],
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    rows: {
      type: [Number, String],
      default: 4
    },
    maxlength: {
      type: [Number, String],
      default: null
    },
    hasError: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isFocused: false,
      inputId: `glass-textarea-${++textareaCounter}`
    };
  },
  computed: {
    charCount() {
      return this.modelValue ? this.modelValue.length : 0;
    },
    isAtLimit() {
      return this.maxlength && this.charCount >= this.maxlength;
    }
  },
  methods: {
    onInput(e) {
      this.$emit('update:modelValue', e.target.value);
    },
    onFocus(e) {
      this.isFocused = true;
      this.$emit('focus', e);
    },
    onBlur(e) {
      this.isFocused = false;
      this.$emit('blur', e);
    },
    focus() {
      this.$refs.textarea.focus();
    }
  }
};
</script>

<style scoped>
.glass-textarea {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.textarea-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text);
}

.required {
  color: var(--color-danger);
  margin-left: 0.25rem;
}

.textarea-wrapper {
  position: relative;
}

.textarea-field {
  width: 100%;
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--border-radius);
  color: var(--color-text);
  font-size: 0.9375rem;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  transition: all 0.2s ease;
}

.textarea-field::placeholder {
  color: var(--color-text-muted);
}

.textarea-field:focus {
  outline: none;
  border-color: var(--theme-primary);
  box-shadow: 0 0 0 3px rgba(59, 136, 165, 0.15);
}

.glass-textarea.is-disabled .textarea-field {
  opacity: 0.6;
  cursor: not-allowed;
}

.glass-textarea.has-error .textarea-field {
  border-color: var(--color-danger);
}

.glass-textarea.has-error .textarea-field:focus {
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.15);
}

.char-count {
  position: absolute;
  bottom: 0.5rem;
  right: 0.75rem;
  font-size: 0.75rem;
  color: var(--color-text-muted);
}

.char-count.is-limit {
  color: var(--color-danger);
}

.error-message {
  font-size: 0.8125rem;
  color: var(--color-danger);
}

.description {
  font-size: 0.8125rem;
  color: var(--color-text-muted);
}
</style>
